import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { currentChatTurn } from "../lib/chatTurn.js";
import {
  askHeroNotice,
  askHeroOrigin,
  askHeroPendingRun,
  sendAndAwaitTurn,
} from "../lib/forecast/askHero.js";

/**
 * A question asked on the new-question screen (AskHero). The progress card comes up the moment
 * it is asked, on its first step, while Sayer decides whether it is a forecast and words it
 * (seconds, before any forecast exists); the forecast's own run takes the card over once created.
 * If the question does not become a forecast (not one, no timeframe, a date already past), Sayer's
 * reason is shown on that screen, which the chat panel is closed over.
 *
 * @param {object} deps
 * @param {(text: string, opts: {origin: string, surface?: string}) => Promise<void>} deps.handleChatMessage
 * @param {() => object} deps.getState the run store's live state
 * @param {string|null} deps.activeId the active forecast's id
 * @param {string|null} deps.engine the depth the question will run at, for the card
 * @returns {{pendingRun: object|null, notice: {text: string, question: string}|null,
 *   submit: (q: string, opts?: {example?: string|null}) => Promise<void>,
 *   clearNotice: () => void}}  `example`: the example question last put in the box, so one
 *   asked unchanged is credited to it (askHeroOrigin)
 */
export function useAskHeroSubmit({ handleChatMessage, getState, activeId, engine }) {
  const [pending, setPending] = useState(null); // {question, startedAt}
  const [notice, setNotice] = useState(null);
  // Only the latest question asked here may clear the card or leave a notice.
  const latestRef = useRef(0);

  // The forecast's own run takes the progress card over once it exists, and with a forecast open
  // there is no new-question screen to explain anything on.
  useEffect(() => {
    if (!activeId) return;
    setPending(null);
    setNotice(null);
  }, [activeId, notice]);

  const submit = useCallback(
    async (q, { example = null } = {}) => {
      const ask = ++latestRef.current;
      const since = Date.now();
      setNotice(null);
      setPending({ question: q, startedAt: new Date(since).toISOString() });
      try {
        await sendAndAwaitTurn(
          () => handleChatMessage(q, { origin: askHeroOrigin(q, example), surface: "ask-hero" }),
          currentChatTurn,
        );
      } finally {
        if (ask === latestRef.current) {
          setPending(null);
          setNotice(askHeroNotice(getState(), since, q));
        }
      }
    },
    [handleChatMessage, getState],
  );

  const clearNotice = useCallback(() => setNotice(null), []);
  const pendingRun = useMemo(() => askHeroPendingRun(pending, engine), [pending, engine]);

  return { pendingRun, notice, submit, clearNotice };
}
